import type { components } from '@trackercop/contracts';
import { Select } from '@/components/ui/select';
import { CONSENT_MODES } from '@/lib/constants';

type ConsentMode = components['schemas']['ConsentMode'];
type GTMContainer = components['schemas']['GTMContainer'];

interface CrawlFilterBarProps {
  consentMode: ConsentMode | 'all';
  onConsentModeChange: (mode: ConsentMode | 'all') => void;
  gtmContainers: GTMContainer[];
  gtmContainerId: string | 'all';
  onGtmContainerChange: (id: string | 'all') => void;
}

// Top-level filter bar applied across every crawl-results tab. Writing
// the selected values into URL search params keeps links shareable —
// a reviewer can paste a crawl URL with `?mode=reject-all` and the
// recipient sees the same filtered view.
export function CrawlFilterBar({
  consentMode,
  onConsentModeChange,
  gtmContainers,
  gtmContainerId,
  onGtmContainerChange,
}: CrawlFilterBarProps) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/30 p-3">
      <label className="flex items-center gap-2 text-xs font-medium">
        Consent mode
        <Select
          value={consentMode}
          onChange={(e) => onConsentModeChange(e.target.value as ConsentMode | 'all')}
          className="h-8 w-40"
          data-testid="consent-mode-filter"
        >
          <option value="all">All</option>
          {CONSENT_MODES.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </Select>
      </label>
      <label className="flex items-center gap-2 text-xs font-medium">
        GTM container
        <Select
          value={gtmContainerId}
          onChange={(e) => onGtmContainerChange(e.target.value)}
          className="h-8 w-52"
          data-testid="gtm-container-filter"
        >
          <option value="all">All containers</option>
          {gtmContainers.map((c) => (
            <option key={c.id} value={c.id}>
              {c.containerId}
              {c.version ? ` (v${c.version})` : ''}
            </option>
          ))}
        </Select>
      </label>
    </div>
  );
}
